import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./components/ProtectedRoute";

const API = ""; // same-origin now — Vercel rewrites /api/* to the backend (see client/vercel.json). Keeps the session cookie first-party for Safari/Firefox.

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  // Forgot password states
  const [forgotMode, setForgotMode] = useState(false); // "email" | "otp" | false
  const [fpEmail, setFpEmail] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [fpNewPass, setFpNewPass] = useState("");
  const [fpShowPass, setFpShowPass] = useState(false);
  const [fpLoading, setFpLoading] = useState(false);
  const [fpStep, setFpStep] = useState(1); // 1=email, 2=otp+newpass

  const login = async () => {
    const trimEmail = email.trim().toLowerCase();
    const trimPass = password.trim();
    if (!trimEmail || !trimPass) return alert("Email aur password dono daalo");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimEmail)) return alert("Valid email daalo");
    if (trimPass.length < 6) return alert("Password kam se kam 6 characters");
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimEmail, password: trimPass })
      });
      const data = await res.json();
      if (!res.ok) return alert(data.message || "Login failed");
      localStorage.setItem("subai_session", "1");
      // Confirm the new cookie really works (/api/profile) before leaving this page.
      const s = await auth.refresh();
      if (s !== "authed") return alert("Login ho gaya par session confirm nahi hua. Browser cookies enabled hain? Dobara try karo.");
      const from = location.state?.from;
      navigate(from?.pathname && from.pathname !== "/" && from.pathname !== "/login" ? from.pathname + (from.search || "") : "/dashboard", { replace: true });
    } catch {
      alert("Network error. Dobara try karo.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSendOtp = async () => {
    const trimEmail = fpEmail.trim().toLowerCase();
    if (!trimEmail) return alert("Email daalo");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimEmail)) return alert("Valid email daalo");
    setFpLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimEmail })
      });
      const data = await res.json();
      alert(data.message || "OTP bhej diya gaya 📩");
      setFpStep(2);
    } catch {
      alert("Network error");
    } finally {
      setFpLoading(false);
    }
  };

  const handleForgotReset = async () => {
    const trimOtp = fpOtp.trim();
    const trimPass = fpNewPass.trim();
    const trimEmail = fpEmail.trim().toLowerCase();
    if (!trimOtp || !trimPass) return alert("OTP aur naya password daalo");
    if (!/^\d{6}$/.test(trimOtp)) return alert("OTP 6 digit ka hona chahiye");
    if (trimPass.length < 6) return alert("Password kam se kam 6 characters");
    setFpLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimEmail, otp: trimOtp, newPassword: trimPass })
      });
      const data = await res.json();
      if (!res.ok) return alert(data.message || "Reset failed");
      alert("Password reset ho gaya! Ab login karo ✅");
      setForgotMode(false);
      setFpStep(1);
      setFpEmail(""); setFpOtp(""); setFpNewPass("");
    } catch {
      alert("Network error");
    } finally {
      setFpLoading(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") login(); };

  // ─── FORGOT PASSWORD SCREEN ───
  if (forgotMode) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logo}>Sub<span style={{ color: "#7C5CFF" }}>AI</span></div>
          <h1 style={styles.title}>🔑 Forgot Password</h1>

          {fpStep === 1 && (
            <>
              <p style={{ color: "#aaa", fontSize: 13, textAlign: "center", margin: 0 }}>
                Apna registered email daalo, OTP bhej denge
              </p>
              <input
                placeholder="Email"
                value={fpEmail}
                onChange={(e) => setFpEmail(e.target.value)}
                style={styles.input}
                autoComplete="email"
              />
              <button onClick={handleForgotSendOtp} disabled={fpLoading} style={{ ...styles.btn, opacity: fpLoading ? 0.6 : 1 }}>
                {fpLoading ? "Sending..." : "📩 Send OTP"}
              </button>
            </>
          )}

          {fpStep === 2 && (
            <>
              <p style={{ color: "#aaa", fontSize: 13, textAlign: "center", margin: 0 }}>
                OTP aaya? Daalo aur naya password set karo
              </p>
              <input
                placeholder="6-digit OTP"
                value={fpOtp}
                onChange={(e) => setFpOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                style={{ ...styles.input, letterSpacing: 6, textAlign: "center", fontSize: 20, fontWeight: 700 }}
                maxLength={6}
                inputMode="numeric"
              />

              {/* New password with show/hide */}
              <div style={styles.passWrap}>
                <input
                  type={fpShowPass ? "text" : "password"}
                  placeholder="Naya Password"
                  value={fpNewPass}
                  onChange={(e) => setFpNewPass(e.target.value)}
                  style={styles.inputInner}
                  autoComplete="new-password"
                />
                <span onClick={() => setFpShowPass(!fpShowPass)} style={styles.eyeBtn}>
                  {fpShowPass ? "🙈" : "👁️"}
                </span>
              </div>

              <button onClick={handleForgotReset} disabled={fpLoading} style={{ ...styles.btn, opacity: fpLoading ? 0.6 : 1 }}>
                {fpLoading ? "Resetting..." : "✅ Reset Password"}
              </button>

              <span onClick={() => setFpStep(1)} style={{ ...styles.linkBtn, textAlign: "center", display: "block" }}>
                ← Email change karo
              </span>
            </>
          )}

          <span onClick={() => { setForgotMode(false); setFpStep(1); }} style={{ ...styles.linkBtn, textAlign: "center", display: "block", marginTop: 4 }}>
            ← Login pe wapas jao
          </span>
        </div>
      </div>
    );
  }

  // ─── LOGIN SCREEN ───
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>Sub<span style={{ color: "#7C5CFF" }}>AI</span></div>
        <h1 style={styles.title}>Welcome Back</h1>

        <input
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="email"
          style={styles.input}
        />

        {/* Password with show/hide */}
        <div style={styles.passWrap}>
          <input
            type={showPass ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="current-password"
            style={styles.inputInner}
          />
          <span onClick={() => setShowPass(!showPass)} style={styles.eyeBtn}>
            {showPass ? "🙈" : "👁️"}
          </span>
        </div>

        {/* Forgot Password */}
        <span onClick={() => { setForgotMode(true); setFpEmail(email); }} style={{ ...styles.linkBtn, textAlign: "right", display: "block", marginTop: -4 }}>
          Forgot Password?
        </span>

        <button onClick={login} disabled={loading} style={{ ...styles.btn, opacity: loading ? 0.7 : 1 }}>
          {loading ? "Logging in..." : "Login"}
        </button>

        <p style={styles.link}>
          Don't have an account?{" "}
          <span onClick={() => navigate("/signup")} style={styles.linkBtn}>Signup</span>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#0B0B10", display: "flex", justifyContent: "center", alignItems: "center", fontFamily: "var(--font-ui)", padding: 16 },
  card: { width: 320, maxWidth: "100%", padding: 32, borderRadius: 20, background: "rgba(24,24,31,0.9)", backdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 1px 1px rgba(0,0,0,0.3), 0 24px 48px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column", gap: 14, boxSizing: "border-box" },
  title: { color: "#F5F5F7", textAlign: "center", marginBottom: 6, fontSize: 20, fontWeight: 700 },
  logo: { textAlign: "center", fontSize: 22, fontWeight: 800, letterSpacing: 0.5, color: "#F5F5F7", marginBottom: 2 },
  input: { background: "#1E1E26", border: "1px solid #38383F", borderRadius: 9999, padding: "12px 16px", color: "#F5F5F7", outline: "none", boxSizing: "border-box", fontSize: 14, fontFamily: "var(--font-ui)" },
  passWrap: { display: "flex", alignItems: "center", background: "#1E1E26", border: "1px solid #38383F", borderRadius: 9999, paddingRight: 14 },
  inputInner: { flex: 1, background: "transparent", border: "none", padding: "12px 16px", color: "#F5F5F7", outline: "none", fontSize: 14, fontFamily: "var(--font-ui)" },
  eyeBtn: { cursor: "pointer", fontSize: 16, userSelect: "none" },
  btn: { background: "#7C5CFF", border: "none", padding: "13px", borderRadius: 9999, color: "#F5F5F7", fontWeight: 700, fontSize: 14, textTransform: "uppercase", letterSpacing: 0.6, cursor: "pointer", marginTop: 6, boxShadow: "0 8px 20px rgba(124,92,255,0.3)" },
  link: { color: "#A8A8B3", textAlign: "center", fontSize: 13, margin: 0 },
  linkBtn: { color: "#B3A1FF", cursor: "pointer", fontSize: 13, fontWeight: 600 }
};
